import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import PawPrint from "@/components/PawPrint";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardTitle } from "@/components/ui/card";

const SPECIES_OPTIONS = ["Chat", "Chien", "Lapin", "Oiseau", "Rongeur", "Reptile", "Autre"];
const MONTHS = ["jan","fév","mar","avr","mai","jun","jul","aoû","sep","oct","nov","déc"];

/* ══════════ BILAN SANTÉ QCM ══════════ */
const HEALTH_QUESTIONS = [
  {
    id: "appetite",
    question: "Comment est l'appétit de {name} ?",
    icon: "\uD83C\uDF7D\uFE0F",
    options: [
      { value: "normal", label: "Normal", color: "#27AE60" },
      { value: "reduced", label: "Réduit", color: "#F0A500" },
      { value: "increased", label: "Augmenté", color: "#F0A500" },
      { value: "none", label: "Ne mange plus", color: "#E53535" },
    ],
  },
  {
    id: "energy",
    question: "Quel est son niveau d'énergie ?",
    icon: "\u26A1",
    options: [
      { value: "normal", label: "Normal", color: "#27AE60" },
      { value: "tired", label: "Fatigué", color: "#F0A500" },
      { value: "hyperactive", label: "Hyperactif", color: "#F0A500" },
      { value: "lethargic", label: "Léthargique", color: "#E53535" },
    ],
  },
  {
    id: "weight_condition",
    question: "Comment jugez-vous son poids ?",
    icon: "\u2696\uFE0F",
    options: [
      { value: "normal", label: "Normal", color: "#27AE60" },
      { value: "thin", label: "Trop maigre", color: "#F0A500" },
      { value: "overweight", label: "Surpoids", color: "#F0A500" },
      { value: "obese", label: "Obèse", color: "#E53535" },
    ],
  },
  {
    id: "mood",
    question: "Comment décririez-vous son humeur ?",
    icon: "\uD83D\uDE0A",
    options: [
      { value: "happy", label: "Joyeux / Calme", color: "#27AE60" },
      { value: "anxious", label: "Anxieux / Stressé", color: "#F0A500" },
      { value: "aggressive", label: "Irritable", color: "#E53535" },
      { value: "apathetic", label: "Apathique / Triste", color: "#E53535" },
    ],
  },
  {
    id: "digestion",
    question: "Comment est son transit digestif ?",
    icon: "\uD83D\uDCA9",
    options: [
      { value: "normal", label: "Normal", color: "#27AE60" },
      { value: "diarrhea", label: "Diarrhée", color: "#F0A500" },
      { value: "constipation", label: "Constipation", color: "#F0A500" },
      { value: "vomiting", label: "Vomissements", color: "#E53535" },
    ],
  },
  {
    id: "signs",
    question: "Signes physiques particuliers ?",
    icon: "\uD83D\uDD0D",
    options: [
      { value: "none", label: "Aucun", color: "#27AE60" },
      { value: "scratching", label: "Se gratte", color: "#F0A500" },
      { value: "limping", label: "Boite", color: "#F0A500" },
      { value: "discharge", label: "Écoulements", color: "#E53535" },
    ],
  },
];

const HC_LABEL = {
  normal: "Normal", reduced: "Réduit", increased: "Augmenté", none: "Aucun / Ne mange plus",
  tired: "Fatigué", hyperactive: "Hyperactif", lethargic: "Léthargique",
  thin: "Trop maigre", overweight: "Surpoids", obese: "Obèse",
  happy: "Joyeux / Calme", anxious: "Anxieux", aggressive: "Irritable", apathetic: "Apathique",
  diarrhea: "Diarrhée", constipation: "Constipation", vomiting: "Vomissements",
  scratching: "Se gratte", limping: "Boite", discharge: "Écoulements",
};

const HC_COLOR = {
  normal: "#27AE60", happy: "#27AE60", none: "#E53535",
  reduced: "#F0A500", increased: "#F0A500", tired: "#F0A500", hyperactive: "#F0A500",
  thin: "#F0A500", overweight: "#F0A500", obese: "#E53535",
  anxious: "#F0A500", aggressive: "#E53535", apathetic: "#E53535",
  diarrhea: "#F0A500", constipation: "#F0A500", vomiting: "#E53535",
  scratching: "#F0A500", limping: "#F0A500", discharge: "#E53535",
};

const EMOJI_MAP = { Chat: "🐱", Chien: "🐶", Lapin: "🐰", Oiseau: "🐦", Rongeur: "🐹", Reptile: "🦎", Autre: "🐾" };
const COLORS_MAP = { Chat: "#FFB7C5", Chien: "#FBBF59", Lapin: "#A8D8EA", Oiseau: "#C3E8FF", Rongeur: "#D4C5F0", Reptile: "#B5E8C0", Autre: "#E0E0E0" };

/* ══════════ MINI COMPOSANTS ══════════ */

function StatPill({ icon, value, label, color = "#FF8C42" }) {
  return (
    <div className="bg-white/85 rounded-2xl px-3.5 py-2.5 text-center flex-1 border-[1.5px] border-black/[0.04]">
      <div className="text-[22px] mb-0.5">{icon}</div>
      <div className="font-black text-[15px] leading-none" style={{ color }}>{value}</div>
      <div className="text-[11px] text-app-muted font-semibold mt-0.5">{label}</div>
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <Card className="mb-3.5">
      <CardContent className="pt-[18px]">
        <CardTitle className="mb-3.5">{title}</CardTitle>
        {children}
      </CardContent>
    </Card>
  );
}

/* Graphique poids mini */
function WeightChart({ data, color }) {
  if (!data || data.length === 0) return <div className="text-center text-xs text-app-muted py-3">Aucune donnee de poids</div>;
  const max = Math.max(...data) * 1.1;
  const min = Math.min(...data) * 0.9;
  const W = 220, H = 56;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * W;
    const y = H - ((v - min) / (max - min)) * H;
    return [x, y];
  });
  const path = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x},${y}`).join(" ");
  const areaPath = path + ` L${W},${H} L0,${H} Z`;

  return (
    <div className="overflow-x-auto">
      <svg width={W} height={H + 8} className="block">
        <defs>
          <linearGradient id={`grad-${color}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.25" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#grad-${color})`} />
        <path d={path} stroke={color} strokeWidth="2.5" fill="none" strokeLinejoin="round" strokeLinecap="round" />
        {pts.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={i === pts.length - 1 ? 5 : 3}
            fill={i === pts.length - 1 ? color : "white"} stroke={color} strokeWidth="2" />
        ))}
      </svg>
      <div className="flex justify-between mt-1">
        {MONTHS.slice(Math.max(0, 12 - data.length)).slice(-data.length).map((m, i) => (
          <span key={i} className="text-[9px] text-app-subtle font-bold text-center flex-1">{m}</span>
        ))}
      </div>
    </div>
  );
}

/* Ligne vaccin */
function VaccinRow({ v }) {
  const statusColor = { ok: "#27AE60", warning: "#F0A500", late: "#E53535" };
  const statusLabel = { ok: "À jour", warning: "Bientôt", late: "En retard" };
  const statusBgClass = { ok: "bg-severity-green-bg", warning: "bg-severity-yellow-bg", late: "bg-severity-red-bg" };
  const statusTextClass = { ok: "text-severity-green", warning: "text-severity-yellow", late: "text-severity-red" };

  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-black/[0.04]">
      <div
        className="w-2 h-2 rounded-full shrink-0"
        style={{ background: statusColor[v.status], boxShadow: `0 0 6px ${statusColor[v.status]}88` }}
      />
      <div className="flex-1">
        <div className="font-extrabold text-[13px] text-app-text">{v.name}</div>
        <div className="text-[11px] text-app-muted font-semibold mt-px">Prochain : {v.next}</div>
      </div>
      <span className={cn("rounded-3xl px-2.5 py-0.5 text-[11px] font-extrabold", statusBgClass[v.status], statusTextClass[v.status])}>
        {statusLabel[v.status]}
      </span>
    </div>
  );
}

/* Ligne consultation */
function ConsultRow({ c, onPress }) {
  const severity = c.severity_level || "green";
  const sevColor = { green: "text-severity-green", yellow: "text-severity-yellow", red: "text-severity-red" };
  const sevBgClass = { green: "bg-severity-green-bg", yellow: "bg-severity-yellow-bg", red: "bg-severity-red-bg" };
  const sevLabel = { green: "🟢 OK", yellow: "🟡 Suivi", red: "🔴 Urgent" };

  const date = c.started_at
    ? new Date(c.started_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })
    : "";
  const label = c.summary || "Consultation";

  return (
    <div onClick={onPress} className="flex items-center gap-3 py-[11px] border-b border-black/[0.04] cursor-pointer">
      <div className={cn("w-[42px] h-[42px] rounded-[13px] flex items-center justify-center text-xl shrink-0", sevBgClass[severity])}>
        🩺
      </div>
      <div className="flex-1">
        <div className="font-extrabold text-[13px] text-app-text">{label}</div>
        <div className="text-[11px] text-app-muted font-semibold mt-px">{date}</div>
      </div>
      <span className={cn("rounded-3xl px-2.5 py-0.5 text-[11px] font-extrabold", sevBgClass[severity], sevColor[severity])}>
        {sevLabel[severity]}
      </span>
    </div>
  );
}

/* ══════════ BILAN SANTÉ QCM VIEW ══════════ */
function HealthCheckQCM({ animal, onDone, API }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const name = animal?.name || "votre animal";
  const q = HEALTH_QUESTIONS[step];
  const total = HEALTH_QUESTIONS.length;
  const allDone = Object.keys(answers).length === total;

  const select = (value) => {
    const next = { ...answers, [q.id]: value };
    setAnswers(next);
    if (step < total - 1) {
      setTimeout(() => setStep(step + 1), 250);
    }
  };

  const submit = async () => {
    setSaving(true);
    try {
      const hc = { ...answers, date: new Date().toISOString() };
      await API.put(`/api/animals/${animal.id}`, { health_check: hc });
      onDone(hc);
    } catch {
      onDone(answers);
    }
    setSaving(false);
  };

  const scoreColor = () => {
    const vals = Object.values(answers);
    const bad = vals.filter(v => ["none", "lethargic", "obese", "aggressive", "apathetic", "vomiting", "discharge"].includes(v)).length;
    const warn = vals.filter(v => ["reduced", "increased", "tired", "hyperactive", "thin", "overweight", "anxious", "diarrhea", "constipation", "scratching", "limping"].includes(v)).length;
    if (bad > 0) return { color: "#E53535", label: "Points d'alerte", icon: "\uD83D\uDD34" };
    if (warn > 1) return { color: "#F0A500", label: "À surveiller", icon: "\uD83D\uDFE1" };
    return { color: "#27AE60", label: "Tout va bien !", icon: "\uD83D\uDFE2" };
  };

  return (
    <div className="animate-slide-up">
      <div className="bg-pawcare-gradient px-6 py-5" style={{ paddingTop: "max(20px, env(safe-area-inset-top, 20px))" }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-white/25 flex items-center justify-center text-2xl">
            {"\uD83E\uDE7A"}
          </div>
          <div>
            <div className="font-display text-xl text-white leading-none">Bilan santé</div>
            <div className="text-[11px] text-white/75 font-semibold">{name} — {step + 1}/{total}</div>
          </div>
        </div>
        <div className="h-1.5 rounded-full bg-white/25 overflow-hidden">
          <div
            className="h-full rounded-full bg-white transition-[width] duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
            style={{ width: `${((allDone ? total : step) / total) * 100}%` }}
          />
        </div>
      </div>

      <div className="px-5 pt-6 pb-24 max-w-[540px] mx-auto">
        {!allDone ? (
          <div key={q.id} className="animate-fade-in">
            <div className="text-center mb-6">
              <div className="text-4xl mb-3">{q.icon}</div>
              <h2 className="font-black text-lg text-app-text">
                {q.question.replace("{name}", name)}
              </h2>
              <p className="text-xs text-app-muted font-semibold mt-1">Question {step + 1} sur {total}</p>
            </div>

            <div className="flex flex-col gap-2.5">
              {q.options.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => select(opt.value)}
                  className={cn(
                    "w-full py-4 px-5 rounded-2xl border-[2px] font-extrabold text-[15px] cursor-pointer transition-all duration-200 text-left flex items-center gap-3",
                    answers[q.id] === opt.value
                      ? "text-white scale-[1.02]"
                      : "bg-white text-app-text hover:scale-[1.01]",
                  )}
                  style={{
                    borderColor: answers[q.id] === opt.value ? opt.color : "#E8E8E8",
                    background: answers[q.id] === opt.value ? opt.color : undefined,
                    boxShadow: answers[q.id] === opt.value ? `0 4px 16px ${opt.color}55` : "0 2px 8px rgba(0,0,0,0.04)",
                  }}
                >
                  <div
                    className="w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center"
                    style={{ borderColor: answers[q.id] === opt.value ? "white" : opt.color }}
                  >
                    {answers[q.id] === opt.value && (
                      <div className="w-2.5 h-2.5 rounded-full bg-white" />
                    )}
                  </div>
                  {opt.label}
                </button>
              ))}
            </div>

            {step > 0 && (
              <button
                onClick={() => setStep(step - 1)}
                className="mt-4 w-full py-2.5 bg-transparent border-none cursor-pointer font-bold text-sm text-app-muted"
              >
                {"\u2190"} Question précédente
              </button>
            )}
          </div>
        ) : (
          <div className="animate-fade-in">
            <div className="text-center mb-6">
              <div className="text-5xl mb-3">{scoreColor().icon}</div>
              <h2 className="font-black text-xl text-app-text">{scoreColor().label}</h2>
              <p className="text-sm text-app-muted font-semibold mt-1">Bilan de {name}</p>
            </div>

            <div className="bg-white rounded-3xl p-4 shadow-soft border-[1.5px] border-black/5 mb-5">
              {HEALTH_QUESTIONS.map((q) => (
                <div key={q.id} className="flex items-center gap-3 py-2.5 border-b border-black/[0.04] last:border-b-0">
                  <span className="text-lg">{q.icon}</span>
                  <span className="flex-1 text-[13px] font-bold text-app-text">
                    {q.question.replace("{name}", name).replace(" ?", "")}
                  </span>
                  <span
                    className="text-[12px] font-extrabold rounded-full px-2.5 py-0.5"
                    style={{
                      color: HC_COLOR[answers[q.id]] || "#27AE60",
                      background: (HC_COLOR[answers[q.id]] || "#27AE60") + "18",
                    }}
                  >
                    {HC_LABEL[answers[q.id]] || answers[q.id]}
                  </span>
                </div>
              ))}
            </div>

            <button
              onClick={submit}
              disabled={saving}
              className="w-full py-4 rounded-[18px] border-none cursor-pointer bg-pawcare-gradient text-white font-black text-base shadow-pawcare flex items-center justify-center gap-2.5"
            >
              {saving ? "Enregistrement..." : <>{"\u2705"} Enregistrer le bilan</>}
            </button>

            <button
              onClick={() => { setStep(0); setAnswers({}); }}
              className="mt-3 w-full py-2.5 bg-transparent border-[1.5px] border-gray-200 rounded-2xl cursor-pointer font-bold text-sm text-app-muted"
            >
              {"\u21A9"} Recommencer le bilan
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════ RÉSUMÉ BILAN DANS FICHE ══════════ */
function HealthCheckSummary({ healthCheck, name, onRedo }) {
  if (!healthCheck || !healthCheck.appetite) {
    return (
      <div className="bg-white rounded-3xl p-5 shadow-soft border-[1.5px] border-black/5 mb-3.5 text-center">
        <div className="text-3xl mb-2">{"\uD83E\uDE7A"}</div>
        <div className="font-black text-sm text-app-text mb-1">Aucun bilan santé</div>
        <p className="text-xs text-app-muted font-semibold mb-3">
          Faites un bilan rapide pour suivre la santé de {name}.
        </p>
        <button
          onClick={onRedo}
          className="bg-pawcare-gradient text-white font-black text-sm px-5 py-2.5 rounded-2xl border-none cursor-pointer shadow-pawcare"
        >
          Faire le bilan santé
        </button>
      </div>
    );
  }

  const vals = Object.entries(healthCheck).filter(([k]) => k !== "date");
  const bad = vals.filter(([, v]) => ["none", "lethargic", "obese", "aggressive", "apathetic", "vomiting", "discharge"].includes(v)).length;
  const warn = vals.filter(([, v]) => ["reduced", "increased", "tired", "hyperactive", "thin", "overweight", "anxious", "diarrhea", "constipation", "scratching", "limping"].includes(v)).length;
  const scoreIcon = bad > 0 ? "\uD83D\uDD34" : warn > 1 ? "\uD83D\uDFE1" : "\uD83D\uDFE2";
  const scoreLabel = bad > 0 ? "Points d'alerte" : warn > 1 ? "À surveiller" : "Tout va bien";
  const scoreColor = bad > 0 ? "#E53535" : warn > 1 ? "#F0A500" : "#27AE60";
  const dateStr = healthCheck.date
    ? new Date(healthCheck.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
    : "";

  return (
    <div className="bg-white rounded-3xl p-4 shadow-soft border-[1.5px] border-black/5 mb-3.5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">{scoreIcon}</span>
          <div>
            <div className="font-black text-sm" style={{ color: scoreColor }}>{scoreLabel}</div>
            {dateStr && <div className="text-[10px] text-app-muted font-semibold">{dateStr}</div>}
          </div>
        </div>
        <button
          onClick={onRedo}
          className="bg-[#FFF4E8] border-[1.5px] border-pawcare-orange/30 rounded-xl px-3 py-1.5 font-extrabold text-[11px] text-pawcare-orange-dark cursor-pointer"
        >
          Refaire le bilan
        </button>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {HEALTH_QUESTIONS.map((q) => {
          const val = healthCheck[q.id];
          if (!val) return null;
          return (
            <div key={q.id} className="flex items-center gap-1.5 py-1 px-2 rounded-lg bg-gray-50">
              <span className="text-sm">{q.icon}</span>
              <span
                className="text-[11px] font-bold truncate"
                style={{ color: HC_COLOR[val] || "#27AE60" }}
              >
                {HC_LABEL[val] || val}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ══════════ FORMULAIRE AJOUT ANIMAL ══════════ */
function AddAnimalForm({ onSave, onCancel, API }) {
  const [form, setForm] = useState({ name: "", species: "", breed: "", sex: "", birthdate: "", weight: "", sterilized: false, notes: "", photo_url: null });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const valid = form.name.trim() && form.species;

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert("Image trop volumineuse (max 5 Mo)"); return; }
    if (!file.type.startsWith("image/")) { alert("Veuillez s\u00e9lectionner une image"); return; }

    // Preview
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await API.post("/api/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo_url", res.data.url);
    } catch {
      alert("Erreur lors de l'upload de la photo");
      setPhotoPreview(null);
    }
    setUploading(false);
  };

  return (
    <div className="animate-slide-up">
      {/* En-tete */}
      <div className="bg-pawcare-gradient px-6 py-5 flex items-center justify-between" style={{ paddingTop: "max(20px, env(safe-area-inset-top, 20px))" }}>
        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            className="bg-white/20 border-[1.5px] border-white/35 rounded-xl w-9 h-9 flex items-center justify-center cursor-pointer text-lg text-white"
          >
            ←
          </button>
          <div className="font-display text-[22px] text-white">Nouveau loulou</div>
        </div>
        <div className="text-[32px]">{EMOJI_MAP[form.species] || "🐾"}</div>
      </div>

      <div className="px-5 pt-5 pb-24 max-w-[540px] mx-auto flex flex-col gap-[18px]">

        {/* Photo */}
        <div className="flex flex-col items-center gap-2">
          <label className="cursor-pointer">
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handlePhoto} />
            <div className={cn(
              "w-24 h-24 rounded-full border-[3px] border-dashed flex items-center justify-center overflow-hidden transition-all duration-200",
              photoPreview ? "border-pawcare-orange" : "border-gray-200 hover:border-pawcare-orange/50",
            )}>
              {photoPreview ? (
                <img src={photoPreview} alt="preview" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center">
                  <div className="text-2xl">{"\uD83D\uDCF7"}</div>
                  <div className="text-[10px] text-app-muted font-bold">Photo</div>
                </div>
              )}
            </div>
          </label>
          {uploading && <div className="text-xs text-pawcare-orange font-bold animate-pulse">Upload en cours...</div>}
        </div>

        {/* Espece */}
        <div>
          <label className="font-extrabold text-[13px] text-app-text block mb-1.5">Espèce *</label>
          <div className="flex flex-wrap gap-2">
            {SPECIES_OPTIONS.map(sp => (
              <button
                key={sp}
                onClick={() => set("species", sp)}
                className={cn(
                  "px-4 py-2 rounded-3xl border-[1.5px] font-extrabold text-[13px] cursor-pointer transition-all duration-200",
                  form.species === sp ? "text-app-text" : "text-app-muted border-gray-200 bg-white"
                )}
                style={{
                  borderColor: form.species === sp ? (COLORS_MAP[sp] || "#FFB347") : undefined,
                  background: form.species === sp ? `${COLORS_MAP[sp] || "#FFB347"}30` : undefined,
                }}
              >
                {EMOJI_MAP[sp] || "🐾"} {sp}
              </button>
            ))}
          </div>
        </div>

        {/* Nom */}
        <div>
          <label className="font-extrabold text-[13px] text-app-text block mb-1.5">Prénom *</label>
          <Input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Ex : Noisette" />
        </div>

        {/* Race */}
        <div>
          <label className="font-extrabold text-[13px] text-app-text block mb-1.5">Race / variété</label>
          <Input value={form.breed} onChange={e => set("breed", e.target.value)} placeholder="Ex : Maine Coon, Labrador…" />
        </div>

        {/* Sexe */}
        <div>
          <label className="font-extrabold text-[13px] text-app-text block mb-1.5">Sexe</label>
          <div className="flex gap-2.5">
            {[{ label: "Male", value: "male", icon: "\u2642\uFE0F" }, { label: "Femelle", value: "femelle", icon: "\u2640\uFE0F" }].map(s => (
              <button
                key={s.value}
                onClick={() => set("sex", s.value)}
                className={cn(
                  "flex-1 py-2.5 rounded-2xl border-[1.5px] font-extrabold text-sm cursor-pointer transition-all duration-200 flex items-center justify-center gap-1.5",
                  form.sex === s.value ? "border-pawcare-orange bg-[#FFF4E8] text-app-text" : "border-gray-200 bg-white text-app-muted"
                )}
              >
                {s.icon} {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Date de naissance + poids */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="font-extrabold text-[13px] text-app-text block mb-1.5">Date de naissance</label>
            <Input type="date" value={form.birthdate} onChange={e => set("birthdate", e.target.value)} />
          </div>
          <div>
            <label className="font-extrabold text-[13px] text-app-text block mb-1.5">Poids actuel</label>
            <div className="relative">
              <Input value={form.weight} onChange={e => set("weight", e.target.value)} placeholder="0.0" className="pr-9" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-app-muted font-bold text-[13px]">kg</span>
            </div>
          </div>
        </div>

        {/* Sterilise */}
        <div>
          <label className="font-extrabold text-[13px] text-app-text block mb-1.5">Stérilisé(e) ?</label>
          <div className="flex gap-2.5">
            {["Oui", "Non"].map(v => {
              const isActive = (form.sterilized && v === "Oui") || (!form.sterilized && v === "Non");
              return (
                <button
                  key={v}
                  onClick={() => set("sterilized", v === "Oui")}
                  className={cn(
                    "flex-1 py-2.5 rounded-2xl border-[1.5px] font-extrabold text-sm text-app-text cursor-pointer transition-all duration-200",
                    isActive ? "border-pawcare-orange bg-[#FFF4E8]" : "border-gray-200 bg-white"
                  )}
                >
                  {v}
                </button>
              );
            })}
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="font-extrabold text-[13px] text-app-text block mb-1.5">Notes & antécédents</label>
          <textarea
            value={form.notes}
            onChange={e => set("notes", e.target.value)}
            placeholder="Allergies connues, traitements en cours, habitudes…"
            rows={3}
            className="w-full px-4 py-3.5 rounded-2xl border-[1.5px] border-gray-200 text-[15px] font-bold text-app-text bg-white outline-none transition-all duration-200 focus:border-pawcare-orange focus:ring-[3px] focus:ring-pawcare-orange/15 placeholder:text-gray-300 placeholder:font-semibold resize-none leading-relaxed"
          />
        </div>

        {/* Bouton */}
        <Button
          disabled={!valid}
          onClick={() => valid && onSave(form)}
          className={cn(
            "w-full py-4 text-base",
            !valid && "bg-gray-200 text-gray-400 shadow-none cursor-not-allowed"
          )}
        >
          <span className="text-xl">🐾</span>
          Ajouter {form.name || "mon animal"}
        </Button>
      </div>
    </div>
  );
}

/* ══════════ FORMULAIRE EDITION ANIMAL ══════════ */
function EditAnimalForm({ animal, onSave, onCancel, API }) {
  const [form, setForm] = useState({
    name: animal.name || "",
    species: animal.species || "",
    breed: animal.breed || "",
    sex: animal.sex || "",
    birthdate: animal.birthdate || "",
    weight: animal.weight_kg ? String(animal.weight_kg) : "",
    sterilized: animal.sterilized || false,
    notes: animal.medical_history || animal.notes || "",
    photo_url: animal.photo_url || null,
    vet_name: animal.vet_name || "",
    vet_phone: animal.vet_phone || "",
    microchip: animal.microchip || "",
  });
  const [photoPreview, setPhotoPreview] = useState(animal.photo_url || null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const valid = form.name.trim() && form.species;

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert("Image trop volumineuse (max 5 Mo)"); return; }
    if (!file.type.startsWith("image/")) { alert("Veuillez sélectionner une image"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await API.post("/api/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo_url", res.data.url);
    } catch {
      alert("Erreur lors de l'upload de la photo");
      setPhotoPreview(animal.photo_url || null);
    }
    setUploading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        species: form.species,
        breed: form.breed || null,
        sex: form.sex || null,
        birthdate: form.birthdate || null,
        weight_kg: form.weight ? parseFloat(form.weight) : null,
        sterilized: form.sterilized,
        medical_history: form.notes || null,
        photo_url: form.photo_url || null,
        vet_name: form.vet_name || null,
        vet_phone: form.vet_phone || null,
        microchip: form.microchip || null,
      };
      const res = await API.put(`/api/animals/${animal.id}`, payload);
      onSave(res.data);
    } catch (err) {
      alert("Erreur : " + (err.response?.data?.detail || "Erreur inconnue"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="animate-slide-up">
      <div className="bg-pawcare-gradient px-6 py-5 flex items-center justify-between" style={{ paddingTop: "max(20px, env(safe-area-inset-top, 20px))" }}>
        <div className="flex items-center gap-3">
          <button onClick={onCancel} className="bg-white/20 border-[1.5px] border-white/35 rounded-xl w-9 h-9 flex items-center justify-center cursor-pointer text-lg text-white">←</button>
          <div className="font-display text-[22px] text-white">Modifier {animal.name}</div>
        </div>
        <div className="text-[32px]">{EMOJI_MAP[form.species] || "🐾"}</div>
      </div>

      <div className="px-5 pt-5 pb-24 max-w-[540px] mx-auto flex flex-col gap-[18px]">
        {/* Photo */}
        <div className="flex flex-col items-center gap-2">
          <label className="cursor-pointer">
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handlePhoto} />
            <div className={cn("w-24 h-24 rounded-full border-[3px] border-dashed flex items-center justify-center overflow-hidden transition-all duration-200", photoPreview ? "border-pawcare-orange" : "border-gray-200 hover:border-pawcare-orange/50")}>
              {photoPreview ? <img src={photoPreview} alt="preview" className="w-full h-full object-cover" /> : <div className="text-center"><div className="text-2xl">📷</div><div className="text-[10px] text-app-muted font-bold">Photo</div></div>}
            </div>
          </label>
          {uploading && <div className="text-xs text-pawcare-orange font-bold animate-pulse">Upload en cours...</div>}
        </div>

        {/* Espèce */}
        <div>
          <label className="font-extrabold text-[13px] text-app-text block mb-1.5">Espèce *</label>
          <div className="flex flex-wrap gap-2">
            {SPECIES_OPTIONS.map(sp => (
              <button key={sp} onClick={() => set("species", sp)} className={cn("px-4 py-2 rounded-3xl border-[1.5px] font-extrabold text-[13px] cursor-pointer transition-all duration-200", form.species === sp ? "text-app-text" : "text-app-muted border-gray-200 bg-white")} style={{ borderColor: form.species === sp ? (COLORS_MAP[sp] || "#FFB347") : undefined, background: form.species === sp ? `${COLORS_MAP[sp] || "#FFB347"}30` : undefined }}>
                {EMOJI_MAP[sp] || "🐾"} {sp}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="font-extrabold text-[13px] text-app-text block mb-1.5">Prénom *</label>
          <Input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Ex : Noisette" />
        </div>

        <div>
          <label className="font-extrabold text-[13px] text-app-text block mb-1.5">Race / variété</label>
          <Input value={form.breed} onChange={e => set("breed", e.target.value)} placeholder="Ex : Maine Coon, Labrador…" />
        </div>

        <div>
          <label className="font-extrabold text-[13px] text-app-text block mb-1.5">Sexe</label>
          <div className="flex gap-2.5">
            {[{ label: "Male", value: "male", icon: "♂️" }, { label: "Femelle", value: "femelle", icon: "♀️" }].map(s => (
              <button key={s.value} onClick={() => set("sex", s.value)} className={cn("flex-1 py-2.5 rounded-2xl border-[1.5px] font-extrabold text-sm cursor-pointer transition-all duration-200 flex items-center justify-center gap-1.5", form.sex === s.value ? "border-pawcare-orange bg-[#FFF4E8] text-app-text" : "border-gray-200 bg-white text-app-muted")}>
                {s.icon} {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="font-extrabold text-[13px] text-app-text block mb-1.5">Date de naissance</label>
            <Input type="date" value={form.birthdate} onChange={e => set("birthdate", e.target.value)} />
          </div>
          <div>
            <label className="font-extrabold text-[13px] text-app-text block mb-1.5">Poids actuel</label>
            <div className="relative">
              <Input value={form.weight} onChange={e => set("weight", e.target.value)} placeholder="0.0" className="pr-9" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-app-muted font-bold text-[13px]">kg</span>
            </div>
          </div>
        </div>

        <div>
          <label className="font-extrabold text-[13px] text-app-text block mb-1.5">Stérilisé(e) ?</label>
          <div className="flex gap-2.5">
            {["Oui", "Non"].map(v => {
              const isActive = (form.sterilized && v === "Oui") || (!form.sterilized && v === "Non");
              return (
                <button key={v} onClick={() => set("sterilized", v === "Oui")} className={cn("flex-1 py-2.5 rounded-2xl border-[1.5px] font-extrabold text-sm text-app-text cursor-pointer transition-all duration-200", isActive ? "border-pawcare-orange bg-[#FFF4E8]" : "border-gray-200 bg-white")}>
                  {v}
                </button>
              );
            })}
          </div>
        </div>

        {/* Vétérinaire */}
        <div>
          <label className="font-extrabold text-[13px] text-app-text block mb-1.5">🏥 Vétérinaire</label>
          <div className="flex flex-col gap-2">
            <Input value={form.vet_name} onChange={e => set("vet_name", e.target.value)} placeholder="Nom du vétérinaire / clinique" />
            <Input value={form.vet_phone} onChange={e => set("vet_phone", e.target.value)} placeholder="Téléphone" type="tel" />
          </div>
        </div>

        {/* Puce */}
        <div>
          <label className="font-extrabold text-[13px] text-app-text block mb-1.5">🔖 Puce électronique</label>
          <Input value={form.microchip} onChange={e => set("microchip", e.target.value)} placeholder="N° de puce (15 chiffres)" />
        </div>

        <div>
          <label className="font-extrabold text-[13px] text-app-text block mb-1.5">Notes & antécédents</label>
          <textarea value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Allergies connues, traitements en cours…" rows={3} className="w-full px-4 py-3.5 rounded-2xl border-[1.5px] border-gray-200 text-[15px] font-bold text-app-text bg-white outline-none transition-all duration-200 focus:border-pawcare-orange focus:ring-[3px] focus:ring-pawcare-orange/15 placeholder:text-gray-300 placeholder:font-semibold resize-none leading-relaxed" />
        </div>

        <Button disabled={!valid || saving} onClick={handleSave} className={cn("w-full py-4 text-base", (!valid || saving) && "bg-gray-200 text-gray-400 shadow-none cursor-not-allowed")}>
          {saving ? "Enregistrement..." : <>✅ Enregistrer les modifications</>}
        </Button>
      </div>
    </div>
  );
}

/* ══════════ MODAL AJOUT VACCIN ══════════ */
function AddVaccinModal({ animal, onSave, onCancel, API }) {
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim() || !date) return;
    setSaving(true);
    try {
      const vaccins = [...(animal.vaccins || []), { name: name.trim(), date, next: nextDate || "Non planifié", status: nextDate ? "ok" : "warning" }];
      await API.put(`/api/animals/${animal.id}`, { health_check: { ...(animal.health_check || {}), vaccins } });
      onSave(vaccins);
    } catch {
      alert("Erreur lors de l'ajout du vaccin");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm animate-fade-in" onClick={onCancel}>
      <div className="bg-white rounded-t-3xl w-full max-w-[540px] p-5 pb-8 animate-slide-up" onClick={e => e.stopPropagation()}>
        <div className="w-10 h-1 rounded-full bg-gray-200 mx-auto mb-4" />
        <h3 className="font-black text-lg text-app-text mb-4">💉 Ajouter un vaccin</h3>

        <div className="flex flex-col gap-3.5">
          <div>
            <label className="font-extrabold text-[13px] text-app-text block mb-1.5">Nom du vaccin *</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Ex : Rage, Typhus, Leucose…" />
          </div>
          <div>
            <label className="font-extrabold text-[13px] text-app-text block mb-1.5">Date d'administration *</label>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div>
            <label className="font-extrabold text-[13px] text-app-text block mb-1.5">Prochain rappel</label>
            <Input type="date" value={nextDate} onChange={e => setNextDate(e.target.value)} />
          </div>

          <Button disabled={!name.trim() || !date || saving} onClick={handleSave} className="w-full py-4 text-base mt-2">
            {saving ? "Enregistrement..." : "💉 Ajouter le vaccin"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ══════════ VUE DETAIL ══════════ */
function AnimalDetail({ animal, onBack, onEdit, onHealthCheck, onAddVaccin, onNewConsult }) {
  const [tab, setTab] = useState("sante");

  return (
    <div className="animate-slide-up">

      {/* Header animal */}
      <div className="px-5 relative" style={{ background: animal.bg, paddingTop: "max(20px, env(safe-area-inset-top, 20px))" }}>
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={onBack}
            className="bg-white/70 border-none rounded-xl w-9 h-9 flex items-center justify-center cursor-pointer text-lg"
          >
            ←
          </button>
          <button
            onClick={onEdit}
            className="bg-white/70 border-none rounded-xl px-3.5 py-1.5 flex items-center gap-1.5 cursor-pointer font-extrabold text-[13px] text-gray-500"
          >
            ✏️ Modifier
          </button>
        </div>

        <div className="flex items-end gap-4 pb-6">
          <div className="w-[90px] h-[90px] rounded-full bg-white/60 flex items-center justify-center text-5xl border-[3px] border-white/90 shadow-[0_8px_28px_rgba(0,0,0,0.12)] shrink-0 overflow-hidden">
            {animal.photo_url
              ? <img src={animal.photo_url} alt={animal.name} className="w-full h-full object-cover" />
              : animal.emoji}
          </div>
          <div className="pb-1.5">
            <div className="font-display text-[28px] text-app-text leading-none">{animal.name}</div>
            <div className="text-sm text-gray-500 font-bold mt-0.5">{animal.breed} · {animal.species}{animal.sex ? ` · ${animal.sex === "male" ? "♂️" : "♀️"}` : ""}</div>
            <div className="text-xs text-app-muted font-semibold mt-0.5">Né(e) le {animal.birthdate}</div>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-white/55 backdrop-blur-[12px] rounded-t-3xl px-4 pt-4 flex gap-2.5">
          <StatPill icon="⚖️" value={animal.weight} label="Poids" color="#FF8C42" />
          <StatPill icon="🎂" value={animal.age} label="Âge" color="#FF7055" />
          <StatPill icon={animal.sterilized ? "✅" : "⏳"} value={animal.sterilized ? "Oui" : "Non"} label="Stérilisé(e)" color={animal.sterilized ? "#27AE60" : "#F0A500"} />
          <StatPill icon="📋" value={animal.consultations.length} label="Consult." color="#5B8DEF" />
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white/95 border-b-[1.5px] border-black/5 flex px-5">
        {[{ id: "sante", label: "💊 Santé" }, { id: "histoire", label: "📋 Historique" }, { id: "notes", label: "📝 Infos" }].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              "flex-1 py-3.5 px-2 border-none bg-transparent cursor-pointer font-extrabold text-[13px] transition-all duration-200 border-b-[2.5px]",
              tab === t.id
                ? "text-pawcare-orange-dark border-b-pawcare-orange-dark"
                : "text-app-muted border-b-transparent"
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Contenu tabs */}
      <div className="px-4 pt-4 pb-24 max-w-[540px] mx-auto">

        {tab === "sante" && (
          <div className="animate-fade-in">
            <HealthCheckSummary
              healthCheck={animal.health_check}
              name={animal.name}
              onRedo={onHealthCheck}
            />

            <SectionCard title="📈 Suivi du poids">
              <WeightChart data={animal.poids} color={animal.color} />
              <div className="flex justify-between mt-3 px-3.5 py-2.5 bg-gray-50 rounded-xl">
                <span className="text-xs text-app-muted font-bold">Poids actuel</span>
                <span className="font-black text-[15px] text-app-text">{animal.weight}</span>
              </div>
            </SectionCard>

            <SectionCard title="💉 Carnet vaccinal">
              {animal.vaccins.map((v, i) => <VaccinRow key={i} v={v} />)}
              <button
                onClick={onAddVaccin}
                className="w-full mt-3 py-2.5 rounded-2xl border-[1.5px] border-dashed border-gray-200 bg-transparent cursor-pointer font-extrabold text-[13px] text-app-subtle transition-all duration-200 hover:border-pawcare-orange hover:text-pawcare-orange-dark"
              >
                + Ajouter un vaccin
              </button>
            </SectionCard>
          </div>
        )}

        {tab === "histoire" && (
          <div className="animate-fade-in">
            <SectionCard title="🩺 Consultations passées">
              {animal.consultations.map((c, i) => <ConsultRow key={i} c={c} onPress={() => {}} />)}
              {animal.consultations.length === 0 && (
                <div className="text-center py-5 text-app-subtle font-bold text-sm">
                  Aucune consultation pour l'instant.
                </div>
              )}
            </SectionCard>
            <Button onClick={onNewConsult} className="w-full py-4 text-[15px]">
              <span className="text-xl">🩺</span> Nouvelle consultation avec {animal.name}
            </Button>
          </div>
        )}

        {tab === "notes" && (
          <div className="animate-fade-in">
            <SectionCard title="🏥 Suivi vétérinaire">
              <div className="flex flex-col gap-2.5">
                {[
                  { icon: "🏥", label: "Vétérinaire", value: animal.vet },
                  { icon: "🔖", label: "Puce électronique", value: animal.microchip },
                  { icon: animal.sterilized ? "✅" : "⏳", label: "Stérilisation", value: animal.sterilized ? "Oui" : "Non effectuée" },
                ].map((row, i) => (
                  <div key={i} className="flex items-start gap-3 px-3 py-2.5 bg-gray-50 rounded-2xl">
                    <span className="text-xl">{row.icon}</span>
                    <div>
                      <div className="text-[11px] text-app-muted font-bold">{row.label}</div>
                      <div className="text-[13.5px] font-extrabold text-app-text mt-0.5">{row.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>

            <SectionCard title="📝 Notes & antécédents">
              <p className="text-[13.5px] text-gray-500 leading-relaxed font-semibold">
                {animal.notes || "Aucune note pour l'instant."}
              </p>
              <button
                onClick={onEdit}
                className="mt-3 w-full py-2.5 rounded-2xl border-[1.5px] border-dashed border-gray-200 bg-transparent cursor-pointer font-extrabold text-[13px] text-app-subtle transition-all duration-200 hover:border-pawcare-orange hover:text-pawcare-orange-dark"
              >
                ✏️ Modifier les notes
              </button>
            </SectionCard>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════ VUE LISTE ══════════ */
function AnimalList({ animals, onSelect, onAdd }) {
  return (
    <>
      {/* Header */}
      <div className="bg-pawcare-gradient px-6 py-5 flex items-center justify-between shadow-pawcare" style={{ paddingTop: "max(20px, env(safe-area-inset-top, 20px))" }}>
        <div className="flex items-center gap-2.5">
          <PawPrint size={36} color="rgba(255,255,255,0.92)" />
          <div>
            <div className="font-display text-2xl text-white leading-none">Mes animaux</div>
            <div className="text-[11px] text-white/75 font-semibold">
              {animals.length} loulou{animals.length > 1 ? "s" : ""} suivi{animals.length > 1 ? "s" : ""} 🐾
            </div>
          </div>
        </div>
        <button
          onClick={onAdd}
          className="bg-white/20 border-[1.5px] border-white/50 rounded-2xl px-3.5 py-2 text-white font-black text-sm cursor-pointer flex items-center gap-1.5 hover:bg-white/30 transition-colors duration-200"
        >
          + Ajouter
        </button>
      </div>

      {/* Liste */}
      <div className="px-4 pt-5 pb-6 max-w-[540px] mx-auto">
        <div className="flex flex-col gap-3.5">
          {animals.map(a => {
            const lateVaccin = a.vaccins.some(v => v.status === "late");
            const warnVaccin = a.vaccins.some(v => v.status === "warning");
            return (
              <div
                key={a.id}
                onClick={() => onSelect(a)}
                className="bg-white rounded-4xl overflow-hidden shadow-soft border-[1.5px] border-black/[0.04] cursor-pointer transition-all duration-200 hover:-translate-y-[3px] hover:shadow-soft-lg"
              >
                {/* Bande coloree */}
                <div className="px-[18px] py-4 flex items-center gap-3.5" style={{ background: a.bg }}>
                  <div className="w-[68px] h-[68px] rounded-full bg-white/60 flex items-center justify-center text-4xl border-[3px] border-white/90 shrink-0 overflow-hidden">
                    {a.photo_url
                      ? <img src={a.photo_url} alt={a.name} className="w-full h-full object-cover" loading="lazy" />
                      : a.emoji}
                  </div>
                  <div className="flex-1">
                    <div className="font-display text-[22px] text-app-text leading-none">{a.name}</div>
                    <div className="text-[13px] text-gray-500 font-bold mt-0.5">{a.breed} · {a.species}</div>
                    <div className="text-[11px] text-app-muted font-semibold mt-0.5">{a.age} · {a.weight}</div>
                  </div>
                  <div className="text-[22px] text-app-subtle">›</div>
                </div>

                {/* Alertes & stats */}
                <div className="px-[18px] py-3 flex items-center justify-between">
                  <div className="flex gap-2">
                    {lateVaccin && (
                      <span className="bg-severity-red-bg text-severity-red rounded-3xl px-2.5 py-0.5 text-[11px] font-extrabold">
                        💉 Vaccin en retard
                      </span>
                    )}
                    {!lateVaccin && warnVaccin && (
                      <span className="bg-severity-yellow-bg text-severity-yellow rounded-3xl px-2.5 py-0.5 text-[11px] font-extrabold">
                        💉 Rappel bientôt
                      </span>
                    )}
                    {!lateVaccin && !warnVaccin && (
                      <span className="bg-severity-green-bg text-severity-green rounded-3xl px-2.5 py-0.5 text-[11px] font-extrabold">
                        ✓ Vaccins à jour
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 font-bold">
                    {a.consultations.length} consultation{a.consultations.length > 1 ? "s" : ""}
                  </div>
                </div>
              </div>
            );
          })}

          {/* CTA ajouter */}
          <button
            onClick={onAdd}
            className="w-full py-5 rounded-4xl border-2 border-dashed border-pawcare-orange/40 bg-[#FFF8F2]/60 cursor-pointer flex items-center justify-center gap-3 transition-all duration-200 hover:border-pawcare-orange hover:bg-[#FFF4E8]/90"
          >
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-pawcare-orange/20 to-pawcare-coral/15 flex items-center justify-center text-[22px]">
              🐾
            </div>
            <div className="text-left">
              <div className="font-black text-[15px] text-pawcare-orange-dark">Ajouter un animal</div>
              <div className="text-xs text-gray-400 font-semibold">Chat, chien, lapin, NAC…</div>
            </div>
          </button>
        </div>
      </div>
    </>
  );
}

/* Helper: enrichit un animal du backend avec les champs UI */
function enrichAnimal(a, consultations = []) {
  const birthdate = a.birthdate || "";
  let age = "?";
  if (birthdate) {
    const years = new Date().getFullYear() - new Date(birthdate).getFullYear();
    age = years <= 0 ? "< 1 an" : `${years} ans`;
  }
  const vetDisplay = a.vet_name
    ? (a.vet_phone ? `${a.vet_name} — ${a.vet_phone}` : a.vet_name)
    : "Non renseigné";

  return {
    ...a,
    emoji: EMOJI_MAP[a.species] || "\ud83d\udc3e",
    color: COLORS_MAP[a.species] || "#E0E0E0",
    bg: `linear-gradient(135deg,${COLORS_MAP[a.species] || "#E0E0E0"}44,${COLORS_MAP[a.species] || "#E0E0E0"})`,
    age,
    weight: a.weight_kg ? `${a.weight_kg} kg` : "? kg",
    microchip: a.microchip || "Non renseigné",
    vet: vetDisplay,
    notes: a.medical_history || "",
    vaccins: a.health_check?.vaccins || [],
    consultations: consultations,
    poids: a.weight_kg ? [a.weight_kg] : [],
  };
}

/* ══════════ PAGE ══════════ */
export default function AnimauxPage() {
  const { API } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [animals, setAnimals] = useState([]);
  const [view, setView] = useState("list");
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [healthCheckAnimal, setHealthCheckAnimal] = useState(null);
  const [showVaccinModal, setShowVaccinModal] = useState(false);
  const [consultationsMap, setConsultationsMap] = useState({});

  // Fetch animals and consultations from backend
  useEffect(() => {
    Promise.all([
      API.get("/api/animals"),
      API.get("/api/consultations"),
    ]).then(([animalsRes, consultsRes]) => {
      // Group consultations by animal_id
      const cMap = {};
      for (const c of consultsRes.data) {
        const aid = c.animal_id;
        if (!cMap[aid]) cMap[aid] = [];
        cMap[aid].push(c);
      }
      setConsultationsMap(cMap);
      const enriched = animalsRes.data.map(a => enrichAnimal(a, cMap[a.id] || []));
      setAnimals(enriched);

      // Auto-open animal detail if navigated with animalId
      const preselectedId = location.state?.animalId;
      if (preselectedId) {
        const target = enriched.find(a => a.id === preselectedId);
        if (target) {
          setSelected(target);
          setView("detail");
        }
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API]);

  const handleAdd = async (form) => {
    setSaving(true);
    try {
      const res = await API.post("/api/animals", {
        name: form.name,
        species: form.species,
        breed: form.breed || null,
        sex: form.sex || null,
        birthdate: form.birthdate || null,
        weight_kg: form.weight ? parseFloat(form.weight) : null,
        sterilized: form.sterilized,
        medical_history: form.notes || null,
        photo_url: form.photo_url || null,
      });
      const enriched = enrichAnimal(res.data);
      setAnimals(p => [enriched, ...p]);
      // Go to health check after creation
      setHealthCheckAnimal(enriched);
      setView("health_check");
    } catch (err) {
      alert("Erreur lors de l'ajout : " + (err.response?.data?.detail || "Erreur inconnue"));
    } finally {
      setSaving(false);
    }
  };

  const handleHealthCheckDone = (hc) => {
    if (healthCheckAnimal) {
      const updated = { ...healthCheckAnimal, health_check: hc };
      setAnimals(p => p.map(a => a.id === updated.id ? updated : a));
      if (selected?.id === updated.id) setSelected(updated);
    }
    setHealthCheckAnimal(null);
    setView("list");
  };

  const startHealthCheck = (animal) => {
    setHealthCheckAnimal(animal);
    setView("health_check");
  };

  return (
    <div className="min-h-screen bg-app-gradient">

      {view === "list" && (
        <div className="animate-fade-in">
          <AnimalList animals={animals} onSelect={(a) => { setSelected(a); setView("detail"); }} onAdd={() => setView("add")} />
        </div>
      )}

      {view === "detail" && selected && (
        <>
          <AnimalDetail
            animal={selected}
            onBack={() => setView("list")}
            onEdit={() => setView("edit")}
            onHealthCheck={() => startHealthCheck(selected)}
            onAddVaccin={() => setShowVaccinModal(true)}
            onNewConsult={() => navigate("/chat", { state: { animalId: selected.id } })}
          />
          {showVaccinModal && (
            <AddVaccinModal
              animal={selected}
              API={API}
              onCancel={() => setShowVaccinModal(false)}
              onSave={(vaccins) => {
                const updated = { ...selected, vaccins };
                setSelected(updated);
                setAnimals(p => p.map(a => a.id === updated.id ? updated : a));
                setShowVaccinModal(false);
              }}
            />
          )}
        </>
      )}

      {view === "edit" && selected && (
        <EditAnimalForm
          animal={selected}
          API={API}
          onCancel={() => setView("detail")}
          onSave={(data) => {
            const updated = enrichAnimal(data, consultationsMap[data.id] || selected.consultations || []);
            setAnimals(p => p.map(a => a.id === updated.id ? updated : a));
            setSelected(updated);
            setView("detail");
          }}
        />
      )}

      {view === "add" && (
        <AddAnimalForm onSave={handleAdd} onCancel={() => setView("list")} API={API} />
      )}

      {view === "health_check" && healthCheckAnimal && (
        <HealthCheckQCM
          animal={healthCheckAnimal}
          onDone={handleHealthCheckDone}
          API={API}
        />
      )}
    </div>
  );
}
